<script lang="ts">
export default {
  name: 'TextArea',
};
</script>

<script setup lang="ts">
import { computed, ref, onMounted, nextTick, watch } from 'vue';
import { cn } from '@/lib/utils';

interface Props {
  class?: string;
  modelValue?: string;
  PermanentBorder?: boolean;
  autoResize?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  autoResize: false,
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
}>();

const textareaRef = ref<HTMLTextAreaElement>();

const textareaClass = computed(() =>
  cn(
    'flex min-h-[80px] w-full rounded-md bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
    // Conditional border and ring styles based on PermanentBorder prop
    props.PermanentBorder 
      ? 'border-2 ring-2 ring-offset-2' 
      : 'border border-input focus-visible:ring-2 focus-visible:ring-offset-2',
    props.class,
  ),
);

const adjustHeight = () => {
  if (!props.autoResize || !textareaRef.value) return;
  
  const textarea = textareaRef.value;
  
  // Reset height to auto to get the natural height
  textarea.style.height = 'auto';
  
  // Set height to scrollHeight to fit all content
  textarea.style.height = `${textarea.scrollHeight}px`;
  
  // Ensure no overflow
  textarea.style.overflow = 'hidden';
};

onMounted(() => {
  if (props.autoResize) {
    // Use a small timeout to ensure DOM is fully rendered
    setTimeout(() => {
      adjustHeight();
    }, 0);
  }
});

watch(() => props.modelValue, () => {
  if (props.autoResize) {
    nextTick(() => {
      adjustHeight();
    });
  }
});

const handleInput = (event: Event) => {
  const target = event.target as HTMLTextAreaElement;
  emit('update:modelValue', target.value);
  
  if (props.autoResize) {
    // Use requestAnimationFrame for smoother resizing
    requestAnimationFrame(() => {
      adjustHeight();
    });
  }
};
</script>

<template>
  <textarea
    ref="textareaRef"
    :class="textareaClass"
    :value="modelValue"
    @input="handleInput"
    v-bind="$attrs" />
</template>
